import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import {
  Layout,
  Menu,
  Avatar,
  Dropdown,
  Button,
  Drawer,
  Input,
  Tag,
  Empty,
  message,
} from "antd";
import { MenuOutlined, CloseOutlined, DownOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../assets/images/logo.webp";
import postchatRecImg from "../assets/images/postchat-rec.webp";
import NoticePopup from "../componets/NoticePopup";
import NoticeTicker from "../componets/common/NoticeTicker";
import LanguageSwitcher from "../componets/common/LanguageSwitcher";
import { useGlobal } from "../contexts/GlobalContext";
import { useTranslation } from "../i18n";
import { logout, getOpenId } from "../utils/auth";
import request from "../utils/request";
import {
  isNoticeReadLocally,
  markNoticeReadLocally,
} from "../utils/noticeRead";
import { shouldHideNav } from "../config/hideNavConfig";
import {
  PROJECT_NAV,
  GLOBAL_NAV,
  ADMIN_NAV,
  NavGroup,
  NavItem,
  projectPath,
} from "../config/nav";
import { planFromLevel } from "../componets/common/PlanGate";
import { setLastProjectKey } from "../hooks/useCurrentProject";
import { isAdminRole, isSuperAdminRole } from "../utils/roles";
import EmailBindingGate from "../componets/common/EmailBindingGate";

const { Header, Sider, Content } = Layout;

const PLAN_LABEL: Record<string, string> = {
  free: "Free",
  pro: "Pro",
  pro_max: "Pro Max",
  business: "商业版",
};
const PLAN_COLOR: Record<string, string> = {
  free: "default",
  pro: "blue",
  pro_max: "geekblue",
  business: "gold",
};

const LOCK_PREFIX = "__lock__:";

interface ConsoleLayoutProps {
  children: React.ReactNode;
}

const ConsoleLayout: React.FC<ConsoleLayoutProps> = ({ children }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { token, setToken, user, projects, API_BASE_URL } = useGlobal();

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [projectQuery, setProjectQuery] = useState("");
  const [messageApi, contextHolder] = message.useMessage();

  // 公告
  const [notices, setNotices] = useState<
    { id: number; title: string; level: number; is_read?: boolean }[]
  >([]);
  const [unreadNotices, setUnreadNotices] = useState<any[]>([]);
  const [currentUnreadIndex, setCurrentUnreadIndex] = useState(0);
  const [noticePopupVisible, setNoticePopupVisible] = useState(false);

  const openId = getOpenId(token);
  const isAdmin = isAdminRole(user?.apiInfo?.role);
  const isSuperAdmin = isSuperAdminRole(user?.apiInfo?.role);
  const vipInfo = user?.apiInfo?.postchat?.vip_info;
  const plan = planFromLevel(vipInfo?.level, vipInfo?.is_vip);

  const tr = useCallback(
    (item: { i18nKey?: string; label: string }) => {
      if (!item.i18nKey) return item.label;
      const v = t(item.i18nKey);
      // i18next 缺键时回落键名本身 → 视为缺失，用中文 label 兜底（其余语种 P4 补齐）。
      return !v || v === item.i18nKey ? item.label : v;
    },
    [t]
  );

  // ── 当前模式：项目作用域 vs 全局 ───────────────────────────
  const projectKey = useMemo(() => {
    const m = location.pathname.match(/^\/p\/([^/]+)/);
    return m ? decodeURIComponent(m[1]) : undefined;
  }, [location.pathname]);

  const currentProject = useMemo(
    () => projects.find((p) => p.key_value === projectKey),
    [projects, projectKey]
  );
  const inProject = !!projectKey;

  useEffect(() => {
    if (currentProject) setLastProjectKey(currentProject.key_value);
  }, [currentProject]);

  // 项目内当前子路径段（用于菜单高亮）
  const projectSubPath = useMemo(() => {
    if (!projectKey) return "";
    const rest = location.pathname.replace(
      `/p/${encodeURIComponent(projectKey)}/`,
      ""
    );
    return rest.split("/")[0] || "overview";
  }, [location.pathname, projectKey]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ── 公告拉取 ──────────────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await request.get(`${API_BASE_URL}/api/v3/notices`, {
          params: { page: 1, page_size: 3, hidden: 0 },
        });
        if (res.data.code === 200) {
          const list = res.data.data.list || [];
          setNotices(
            list.map((it: any) => ({
              id: it.id,
              title: it.title,
              level: it.level,
              is_read: it.is_read || isNoticeReadLocally(openId, it),
            }))
          );
          const unread = list.filter(
            (it: any) => !it.is_read && !isNoticeReadLocally(openId, it)
          );
          if (unread.length > 0) {
            setUnreadNotices(unread);
            setCurrentUnreadIndex(0);
            setNoticePopupVisible(true);
          }
        }
      } catch (e) {
        console.error("获取公告失败", e);
      }
    })();
  }, [token, API_BASE_URL, openId]);

  const markNoticeRead = useCallback(
    (notice: any) => {
      if (!notice) return;
      markNoticeReadLocally(openId, notice);
      setNotices((prev) =>
        prev.map((it) => (it.id === notice.id ? { ...it, is_read: true } : it))
      );
      request
        .post(`${API_BASE_URL}/api/v3/notices/${notice.id}/read`)
        .catch((e) => console.error("标记公告已读失败", e));
    },
    [API_BASE_URL, openId]
  );

  const handleCloseNotice = () => {
    markNoticeRead(unreadNotices[currentUnreadIndex]);
    setNoticePopupVisible(false);
    if (currentUnreadIndex < unreadNotices.length - 1) {
      setCurrentUnreadIndex((i) => i + 1);
      setTimeout(() => setNoticePopupVisible(true), 300);
    } else {
      setUnreadNotices([]);
      setCurrentUnreadIndex(0);
    }
  };

  // ── 菜单构建 ──────────────────────────────────────────────
  const buildItem = useCallback(
    (item: NavItem, keyPath: string) => {
      const locked = item.postchatOnly && currentProject && !currentProject.is_postchat;
      return {
        key: locked ? `${LOCK_PREFIX}${keyPath}` : keyPath,
        icon: <i className={item.icon} />,
        label: (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            {tr(item)}
            {locked && (
              <i className="ri-lock-2-line" style={{ fontSize: 12, opacity: 0.6 }} />
            )}
          </span>
        ),
      };
    },
    [currentProject, tr]
  );

  const groupsToMenu = useCallback(
    (groups: NavGroup[], resolveKey: (it: NavItem) => string): any[] =>
      groups.flatMap((g): any[] => {
        const children = g.items.map((it) => buildItem(it, resolveKey(it)));
        if (!g.label && !g.i18nKey) return children; // 无标题分组直接平铺
        return [
          {
            key: g.key,
            type: "group" as const,
            label: tr(g),
            children,
          },
        ];
      }),
    [buildItem, tr]
  );

  const menuItems = useMemo<any[]>(() => {
    if (inProject && projectKey) {
      return groupsToMenu(PROJECT_NAV, (it) => projectPath(projectKey, it.path));
    }
    const items = groupsToMenu(GLOBAL_NAV, (it) => it.path);
    if (isAdmin) {
      // superAdminOnly 项对普通 admin 隐藏入口(后端 SuperAdminMiddleware 仍强校验)。
      const visibleAdminGroups = ADMIN_NAV
        .map((g) => ({ ...g, items: g.items.filter((it) => !it.superAdminOnly || isSuperAdmin) }))
        .filter((g) => g.items.length > 0);
      items.push(...groupsToMenu(visibleAdminGroups, (it) => it.path));
    }
    return items;
  }, [inProject, projectKey, isAdmin, isSuperAdmin, groupsToMenu, buildItem, tr]);

  const selectedKey = useMemo(() => {
    if (inProject && projectKey) return projectPath(projectKey, projectSubPath);
    // 全局：取最长匹配
    const all: string[] = [
      ...GLOBAL_NAV.flatMap((g) => g.items.map((i) => i.path)),
      ...ADMIN_NAV.flatMap((g) => g.items.map((i) => i.path)),
    ];
    const matches = all
      .filter((p) => p === "/" ? location.pathname === "/" : location.pathname.startsWith(p))
      .sort((a, b) => b.length - a.length);
    return matches[0] || "/";
  }, [inProject, projectKey, projectSubPath, location.pathname]);

  const onMenuClick = useCallback(
    ({ key }: { key: string }) => {
      if (key.startsWith(LOCK_PREFIX)) {
        messageApi.info("该能力需全功能(Pro)项目，升级后解锁");
        navigate("/payment-checkout?type=member");
      } else {
        navigate(key);
      }
      if (isMobile) setDrawerVisible(false);
    },
    [navigate, isMobile, messageApi]
  );

  // ── 项目切换器 ────────────────────────────────────────────
  const switchProject = (key: string) => {
    setSwitcherOpen(false);
    setProjectQuery("");
    // 项目内：保持当前子页签；否则进概览
    const sub = inProject ? projectSubPath : "overview";
    navigate(projectPath(key, sub));
  };

  const filteredProjects = useMemo(() => {
    const q = projectQuery.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.key_value.toLowerCase().includes(q)
    );
  }, [projects, projectQuery]);

  const projectSwitcher = (
    <Dropdown
      open={switcherOpen}
      onOpenChange={setSwitcherOpen}
      trigger={["click"]}
      placement="bottomLeft"
      dropdownRender={() => (
        <div
          style={{
            width: 300,
            background: "#fff",
            borderRadius: 8,
            boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
            padding: 8,
          }}
        >
          <Input
            placeholder="搜索项目"
            allowClear
            value={projectQuery}
            onChange={(e) => setProjectQuery(e.target.value)}
            prefix={<i className="ri-search-line" />}
            style={{ marginBottom: 8 }}
          />
          <div style={{ maxHeight: 320, overflow: "auto" }}>
            {filteredProjects.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="暂无项目"
                style={{ margin: "16px 0" }}
              />
            ) : (
              filteredProjects.map((p) => (
                <div
                  key={p.key_value}
                  onClick={() => switchProject(p.key_value)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 10px",
                    borderRadius: 6,
                    cursor: "pointer",
                    background:
                      p.key_value === projectKey ? "rgba(57,121,246,0.08)" : "transparent",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "rgba(0,0,0,0.04)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background =
                      p.key_value === projectKey
                        ? "rgba(57,121,246,0.08)"
                        : "transparent")
                  }
                >
                  <span style={{ display: "flex", alignItems: "center", gap: 8, overflow: "hidden" }}>
                    <i
                      className={p.is_postchat ? "ri-rocket-2-line" : "ri-file-text-line"}
                      style={{ color: p.is_postchat ? "#3979F6" : "#52c41a" }}
                    />
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {p.name}
                    </span>
                  </span>
                  <Tag color={p.is_postchat ? "blue" : "green"} style={{ marginInlineEnd: 0 }}>
                    {p.is_postchat ? "全功能" : "仅摘要"}
                  </Tag>
                </div>
              ))
            )}
          </div>
          <div style={{ borderTop: "1px solid #f0f0f0", marginTop: 8, paddingTop: 8, display: "flex", gap: 8 }}>
            <Button
              size="small"
              block
              onClick={() => {
                setSwitcherOpen(false);
                navigate("/");
              }}
            >
              全部项目
            </Button>
            <Button
              size="small"
              type="primary"
              block
              onClick={() => {
                setSwitcherOpen(false);
                navigate("/?new=1");
              }}
            >
              新建项目
            </Button>
          </div>
        </div>
      )}
    >
      <Button
        type="text"
        style={{ display: "flex", alignItems: "center", gap: 6, maxWidth: 220 }}
      >
        <i
          className={
            currentProject?.is_postchat
              ? "ri-rocket-2-line"
              : currentProject
              ? "ri-file-text-line"
              : "ri-apps-2-line"
          }
        />
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {currentProject ? currentProject.name : "选择项目"}
        </span>
        <DownOutlined style={{ fontSize: 10 }} />
      </Button>
    </Dropdown>
  );

  const dropdownItems = [
    { key: "settings", label: t("user.settings"), icon: <i className="ri-settings-3-line" />, onClick: () => navigate("/settings") },
    { key: "orders", label: t("user.orders"), icon: <i className="ri-file-list-line" />, onClick: () => navigate("/my-orders") },
    { key: "usage", label: t("user.usage"), icon: <i className="ri-coin-line" />, onClick: () => navigate("/token-usage") },
    { key: "logout", label: t("user.logout"), icon: <i className="ri-logout-box-line" />, onClick: () => logout(setToken) },
  ];

  const copyUID = () => {
    const uid = openId || "-";
    if (uid === "-") return messageApi.warning("当前没有可用的 UID");
    navigator.clipboard
      .writeText(uid)
      .then(() => messageApi.success("UID 已复制"))
      .catch(() => messageApi.error("复制失败"));
  };

  const menuComponent = (
    <Menu
      theme="dark"
      mode="inline"
      selectedKeys={[selectedKey]}
      items={menuItems}
      onClick={onMenuClick}
      style={{ borderInlineEnd: "none" }}
    />
  );

  const sidebarHeader = (
    <div style={{ padding: "12px 16px" }}>
      <div style={{ height: 40, display: "flex", justifyContent: "center", alignItems: "center", marginBottom: inProject ? 12 : 0 }}>
        <img src={logo} alt="logo" style={{ height: "100%", maxWidth: "100%", objectFit: "contain" }} />
      </div>
      {inProject && (
        <Button
          type="link"
          size="small"
          onClick={() => {
            navigate("/");
            if (isMobile) setDrawerVisible(false);
          }}
          style={{ color: "rgba(255,255,255,0.65)", paddingInline: 0 }}
        >
          <i className="ri-arrow-left-line" style={{ marginRight: 4 }} />
          全部项目
        </Button>
      )}
    </div>
  );

  // ── 全屏页（登录回调/收银台/分享）：不套外壳 ──────────────
  if (shouldHideNav(location.pathname)) {
    return <Content style={{ minHeight: "100vh" }}>{children}</Content>;
  }

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {contextHolder}
      <EmailBindingGate />
      <NoticePopup
        notice={unreadNotices[currentUnreadIndex]}
        visible={noticePopupVisible && unreadNotices.length > 0}
        onClose={handleCloseNotice}
      />

      {!isMobile && (
        <Sider
          style={{ overflow: "auto", height: "100vh", position: "fixed", left: 0, top: 0, bottom: 0 }}
        >
          {sidebarHeader}
          {menuComponent}
        </Sider>
      )}

      <Layout style={{ marginLeft: isMobile ? 0 : 200 }}>
        <Header
          style={{
            padding: "0 16px",
            background: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            position: "fixed",
            width: `calc(100% - ${isMobile ? 0 : 200}px)`,
            zIndex: 1000,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {isMobile && (
              <Button type="text" icon={<MenuOutlined />} onClick={() => setDrawerVisible(true)} />
            )}
            {projectSwitcher}
          </div>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 }}>
            {!isMobile && (
              <NoticeTicker notices={notices} onOpen={(n) => navigate(`/notice?id=${n.id}`)} />
            )}
            <Tag color={PLAN_COLOR[plan]} style={{ marginInlineEnd: 0 }}>
              {PLAN_LABEL[plan]}
            </Tag>
            {!isMobile && (
              <span
                onClick={copyUID}
                title="点击复制 UID"
                style={{ color: "#666", cursor: "pointer", userSelect: "none", fontSize: 14 }}
              >
                UID：{openId || "-"}
              </span>
            )}
            <LanguageSwitcher compact={isMobile} />
            <Dropdown menu={{ items: dropdownItems }} placement="bottomRight" arrow trigger={["hover", "click"]}>
              <Avatar src={postchatRecImg} size={40} style={{ cursor: "pointer" }} />
            </Dropdown>
          </div>
        </Header>

        <Content style={{ marginTop: 64, overflow: "auto" }}>{children}</Content>
      </Layout>

      {isMobile && (
        <Drawer
          placement="left"
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          styles={{
            body: { padding: 0, backgroundColor: "#001529" },
            header: { backgroundColor: "#001529", color: "#fff" },
            wrapper: { backgroundColor: "#001529" },
            mask: { backgroundColor: "rgba(0,0,0,0.45)" },
          }}
          closeIcon={<CloseOutlined style={{ color: "#fff" }} />}
        >
          {sidebarHeader}
          {menuComponent}
        </Drawer>
      )}
    </Layout>
  );
};

export default ConsoleLayout;
